// /account/notifications — "Mi cuenta · Notificaciones". Every person, whatever
// their role, decides here what INHERTA tells them and through which channel. It's
// the third tab of "Mi cuenta" (alongside Perfil and Seguridad) and lives outside
// the owner-only Settings area so an asesor or a visor can reach their own
// preferences. The matrix lists only the events the person's roles can actually
// receive; always-on events show their in-app alert fixed on. Per-cell toggles
// persist immediately via PATCH /account/notification-preferences.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'

import { AccountTabsHeader } from './AccountTabsHeader.js'
import {
  getNotificationPreferences,
  type NotifPrefEvent,
  setNotificationPreference,
} from './notification-preferences-data.js'
import { Chip, Spinner, toast } from '../../design-system/index.js'
import { QueryError } from '../../lib/QueryError.js'

function SectionCard({
  title,
  sub,
  children,
}: {
  title: string
  sub: string
  children: ReactNode
}): JSX.Element {
  return (
    <section className="rounded-xl border border-line bg-surface">
      <div className="border-b border-line px-5 py-4">
        <h2 className="text-[14px] font-bold text-text">{title}</h2>
        <p className="mt-0.5 text-[12px] leading-snug text-text-2">{sub}</p>
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  )
}

function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean
  onChange: () => void
  label: string
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={`relative h-5 w-9 flex-none rounded-full transition-colors ${
        checked ? 'bg-ok' : 'bg-input-line'
      }`}
    >
      <span
        aria-hidden
        className={`absolute top-0.5 size-4 rounded-full bg-white shadow transition-[left] ${
          checked ? 'left-[18px]' : 'left-0.5'
        }`}
      />
    </button>
  )
}

/** A single fixed-on, non-toggleable cell (an always-on event's in-app alert). */
function LockedOn(): JSX.Element {
  return (
    <span
      className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-text-3"
      title="Siempre activo: no se puede desactivar"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="size-3"
      >
        <rect x="4" y="11" width="16" height="9" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
      Siempre
    </span>
  )
}

/** Per-user notification matrix: for each event, which channels reach ME. */
function NotifMatrix(): JSX.Element {
  const qc = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: getNotificationPreferences,
  })
  // Optimistic overlay: { `${type}:${channel}` -> enabled } applied on top of
  // the fetched matrix so a toggle feels instant, reconciled on refetch.
  const [pending, setPending] = useState<Record<string, boolean>>({})

  function toggle(eventType: string, channel: string, next: boolean): void {
    const key = `${eventType}:${channel}`
    setPending((p) => ({ ...p, [key]: next }))
    setNotificationPreference(eventType, channel, next)
      .then(() => qc.invalidateQueries({ queryKey: ['notification-preferences'] }))
      .catch(() => {
        setPending((p) => {
          const { [key]: _drop, ...rest } = p
          return rest
        })
        toast.error('No se pudo guardar', { description: 'Inténtalo de nuevo.' })
      })
  }

  if (isError) {
    return (
      <SectionCard title="Tus notificaciones" sub="Tú decides qué te avisamos y por dónde.">
        <QueryError message="No pudimos cargar tus preferencias." onRetry={() => void refetch()} />
      </SectionCard>
    )
  }
  if (isLoading || !data) {
    return (
      <SectionCard title="Tus notificaciones" sub="Tú decides qué te avisamos y por dónde.">
        <Spinner label="Cargando preferencias…" />
      </SectionCard>
    )
  }

  const enabledOf = (e: NotifPrefEvent, ch: string): boolean =>
    pending[`${e.type}:${ch}`] ?? e.channels[ch]?.enabled ?? false

  return (
    <SectionCard
      title="Tus notificaciones"
      sub="Tú decides qué te avisamos y por dónde. Estas preferencias son solo tuyas."
    >
      <div className="flex flex-col gap-6">
        {data.groups.map((group) => {
          const events = data.events.filter((e) => e.group === group.key)
          if (events.length === 0) return null
          return (
            <div key={group.key}>
              {/* Section header + the channel column labels (desktop). */}
              <div className="mb-2 flex items-center gap-3">
                <h3 className="text-[11px] font-bold uppercase tracking-[0.05em] text-text-3">
                  {group.label}
                </h3>
                <span aria-hidden className="h-px flex-1 bg-line" />
                <div className="hidden gap-4 sm:flex">
                  {data.channels.map((c) => (
                    <span
                      key={c.key}
                      className="w-14 text-center text-[10px] font-bold uppercase tracking-[0.04em] text-text-3"
                    >
                      {c.label}
                    </span>
                  ))}
                </div>
              </div>
              <ul className="overflow-hidden rounded-xl border border-line bg-surface">
                {events.map((e) => (
                  <li
                    key={e.type}
                    className="flex flex-col gap-2.5 border-b border-line px-4 py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-4"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="text-[12.5px] font-semibold text-text">{e.label}</span>
                      {e.alwaysOn && <Chip tone="sens">Siempre</Chip>}
                    </div>
                    <div className="flex items-center gap-6 sm:gap-4">
                      {data.channels.map((c) => {
                        const cell = e.channels[c.key]
                        return (
                          <div
                            key={c.key}
                            className="flex items-center gap-2 sm:w-14 sm:justify-center sm:gap-0"
                          >
                            {/* Channel label: inline on mobile, in the column header on desktop. */}
                            <span className="text-[10.5px] font-semibold uppercase tracking-[0.03em] text-text-3 sm:hidden">
                              {c.label}
                            </span>
                            {cell?.locked ? (
                              <LockedOn />
                            ) : (
                              <Switch
                                checked={enabledOf(e, c.key)}
                                onChange={() => toggle(e.type, c.key, !enabledOf(e, c.key))}
                                label={`${enabledOf(e, c.key) ? 'Desactivar' : 'Activar'} ${e.label} por ${c.label}`}
                              />
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>
    </SectionCard>
  )
}

export function AccountNotificationsPage(): JSX.Element {
  return (
    <div data-testid="account-notifications" className="mx-auto w-full max-w-4xl">
      <AccountTabsHeader active="notificaciones" />
      <div className="mt-5">
        <NotifMatrix />
      </div>
    </div>
  )
}
